import { useRef, useState } from "react";
import { Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const resize = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const size = 320;
    const s = Math.min(img.width, img.height);
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    canvas.getContext("2d").drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, size, size);
    URL.revokeObjectURL(img.src);
    resolve(canvas.toDataURL("image/jpeg", 0.85));
  };
  img.onerror = () => reject(new Error("That file isn't a supported image"));
  img.src = URL.createObjectURL(file);
});

export function Avatar({ user, className = "h-9 w-9", testId }) {
  const initials = (user?.name || user?.email || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return user?.avatar
    ? <img src={user.avatar} alt={user.name || "Profile"} className={`${className} rounded-full object-cover ring-2 ring-white`} data-testid={testId} />
    : <span className={`${className} rounded-full bg-[#1A9AA8] text-white text-xs font-bold flex items-center justify-center`} data-testid={testId}>{initials}</span>;
}

export function AvatarUpload() {
  const { user, reload } = useAuth();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      await api.post("/me/avatar", { data_url: await resize(file) });
      await reload();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err.response ? formatApiError(err) : err.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = () => api.delete("/me/avatar").then(reload).then(() => toast.success("Photo removed"));
  return (
    <div className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-6 flex items-center gap-5" data-testid="avatar-upload">
      <Avatar user={user} className="h-20 w-20 text-lg" testId="avatar-preview" />
      <div className="flex flex-col gap-2">
        <h3 className="font-semibold text-[#0B2A4A]">Profile photo</h3>
        <input ref={input} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="avatar-file-input" />
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => input.current?.click()} disabled={busy} className="inline-flex items-center gap-2 rounded-xl bg-[#0B2A4A] text-[#F5ECD7] px-4 py-2 text-sm font-semibold disabled:opacity-60" data-testid="avatar-upload-btn">
            <Camera className="h-4 w-4" /> {busy ? "Uploading..." : user?.avatar ? "Change photo" : "Upload photo"}
          </button>
          {user?.avatar && <button type="button" onClick={remove} className="inline-flex items-center gap-1 rounded-xl border border-[#0B2A4A]/20 px-3 py-2 text-sm" data-testid="avatar-remove-btn"><Trash2 className="h-4 w-4" /> Remove</button>}
        </div>
        <p className="text-xs text-slate-500">Take a photo or choose one from your phone.</p>
      </div>
    </div>
  );
}
